/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback } from 'react';

import cn from 'classnames';

import { getCookie } from 'libs/node';

import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';

import type { AgentData, TemplatesType } from 'features/agents/helpers/AgentsContext/AgentsContext.types';
import { useAgentsContext } from 'features/agents/helpers/AgentsContext/AgentsContext';
import { agentTypeKeyMap, authConfig, isFreeType, isPaidType, sortByType } from 'features/agents/helpers/utils';

import styles from './CardsAgents.module.scss';

const baseClass = 'cards-agents';

export const iconTypes = {
  check_company_status: '29',
  check_company_registration_number: '20',
  check_vat_number: '30',
  find_company_lei: '13',
  retrieve_company_sic_code: '28',
  competitor_analysis: '3',
  lead_prospector: '19',
  business_credit_score_check: '26',
  credit_limit_advisor: '4',
  check_company_financials: '10',
  check_company_shareholders: '27',
  identify_beneficial_owners: '16',
  business_activity_monitor: '15',
  director_check: '7',
  supply_chain_risk_score: '24',
  payment_behavior_analysis: '1',
  email_finder: '8',
  phone_number_finder: '17',
  check_court_judgment_status: '22',
  total_addressable_market_estimator: '14',
  funding_history_tracker: '11',
  esg_score: '9',
  customer_sentiment_analysis_agent: '5',
  peps_and_sanction_agent: '25',
  intellectual_property_agent: '18',
  company_reputation_score_agent: '23',
  supplier_verification_agent: '31',
  regulatory_compliance_check_agent: '21',
  cybersecurity_risk_assessment_agent: '6',
  ipo_monitor_agent: '12',
  website_traffic_agent: '32',
} as const;

export type AgentName = keyof typeof iconTypes;

type Props = {
  className?: string;
};

type CardProps = {
  active?: boolean;
  card: AgentData;
  onClick: (type: TemplatesType, id?: string) => void;
  type: TemplatesType;
};

const Card = ({ active, card, onClick, type }: CardProps) => {
  const cardIcon = card.name in iconTypes && `company-${iconTypes[card.name as AgentName]}`;

  const onClickHandler = useCallback(() => {
    onClick(isPaidType(card.type) ? 'book-demo' : getCookie(authConfig.ACCESS_KEY) ? 'agent' : 'register', card.slug);
  }, [card.slug, card.type, onClick]);

  if (!cardIcon || active) {
    return null;
  }

  return (
    <li className={styles[`${baseClass}-card`]} onClick={onClickHandler}>
      <div className={styles[`${baseClass}-icon`]}>
        <Icon type={cardIcon} />
      </div>

      <div className={styles[`${baseClass}-inner`]}>
        <div className={styles[`${baseClass}-inner-text`]}>
          <p className={styles[`${baseClass}-title`]}>{card.title}</p>

          <div className={styles[`${baseClass}-description`]} dangerouslySetInnerHTML={{ __html: card.description }} />
        </div>

        <div className={styles[`${baseClass}-link`]}>
          <Button loadingDelay={0} size="md" type="invert">
            Get
            <Icon type="arrow-7" className={styles[`${baseClass}-arrow`]} />
          </Button>
        </div>
      </div>

      {type !== 'agents' && (
        <div className={cn(styles[`${baseClass}-type`], styles[`${baseClass}-type-${card.type.toLowerCase()}`])}>
          {card.type}
        </div>
      )}
    </li>
  );
};

export const CardsAgents = ({ className }: Props) => {
  const { data, updateType, type, agent } = useAgentsContext();

  const renderList = (cards: any[], title?: string) => {
    return (
      <>
        {title && <h2 className={styles[`${baseClass}-header-title`]}>{title}</h2>}

        <ul className={cn(styles[`${baseClass}-cards`], className)}>
          {cards.map((card) => {
            if (isFreeType(card.type) && card.name in agentTypeKeyMap === false) {
              return null;
            }

            return (
              <Card
                key={card.id}
                {...(type !== 'agents' && { active: card.id === agent })}
                card={card}
                onClick={updateType}
                type={type}
              />
            );
          })}
        </ul>
      </>
    );
  };

  if (type === 'agents') {
    const [freeCards, paidCards] = data.reduce<[AgentData[], AgentData[]]>(
      (acc, curr) => {
        if (isFreeType(curr.type)) {
          acc[0].push(curr);
        } else if (isPaidType(curr.type)) {
          acc[1].push(curr);
        }

        return acc;
      },
      [[], []],
    );

    return (
      <>
        {renderList(freeCards, 'Free')}
        {renderList(paidCards, 'Paid')}
      </>
    );
  }

  return renderList([...data].sort(sortByType));
};
